// The active location (plan §4.3): tab-local, taken from the URL slug, resolved against the locations the session can
// open in its account. Pages read its id, zone and currency from here, never from shared state, so two tabs on two
// locations never mix (REG-002/003). Permissions are the session's at this location (the token is switched to it).
import { createContext, useContext, type ReactNode } from 'react';
import type { Currency } from '@radish/primitives';
import type { PermissionKey } from '@radish/contracts';
import { businessToday } from '../lib/dates.ts';
import type { Session } from '../session/session.ts';
import type { LocationChoice } from '@radish/contracts';

type Location = LocationChoice;

interface ActiveLocation {
  readonly locationId: string;
  readonly name: string;
  readonly slug: string;
  readonly timezone: string;
  readonly currency: Currency;
  readonly all: readonly Location[];
  readonly session: Session;
  /** The location's business date now. */
  readonly today: () => string;
  readonly can: (permission: PermissionKey) => boolean;
  /** Radish staff: may edit the account-level settings merchants only read (legacy "AR" fields). */
  readonly isPlatformAdmin: boolean;
}

const Context = createContext<ActiveLocation | null>(null);

export function activeLocationOf(location: Location, all: readonly Location[], session: Session): ActiveLocation {
  const perms = new Set(session.view.current.permissions);
  return {
    locationId: location.locationId,
    name: location.name,
    slug: location.slug,
    timezone: location.timezone,
    currency: location.currency as Currency,
    all,
    session,
    today: () => businessToday(location.timezone),
    can: (permission) => perms.has(permission),
    isPlatformAdmin: perms.has('settings.platform.manage'),
  };
}

export function ActiveLocationProvider({ value, children }: { readonly value: ActiveLocation; readonly children: ReactNode }) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useActiveLocation(): ActiveLocation {
  const value = useContext(Context);
  if (value === null) throw new Error('useActiveLocation is only available under /l/$slug');
  return value;
}
